import { ReloadOutlined } from '@ant-design/icons';
import {
    Alert,
    Button,
    Col,
    Form,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { describeLabelStockOption, type LabelStockOption } from '../domain/labelPrinterConfig';
import type { PrinterHealth, PrinterRow } from '../domain/printerSelection';
import { usePrinterSelection } from '../hooks/usePrinterSelection';

const { Text } = Typography;

/** Which Ant Design status colour each honest state gets. */
const HEALTH_COLOUR: Record<PrinterHealth, string> = {
    ready: 'green',
    offline: 'orange',
    unavailable: 'red',
    error: 'red',
    unknown: 'default',
};

/**
 * The administrative view of this workstation's label printing.
 *
 * Shows discovery, connection state, the preferred printer, the effective printer and failover —
 * and lets an administrator change the selection. Not read-only, and not a second selection
 * system: it writes through the same `/v1/printers/select` endpoint the Listings quick selector
 * uses, so a change in either place is visible in the other.
 *
 * **Scoped to this workstation.** The printers listed are the ones attached to the machine the
 * browser is running on, because the local service is the only thing that can see them. No remote
 * management protocol exists, so this deliberately does not offer to configure another counter's
 * hardware — the cash point selector below applies to the label *stock*, which is a shop-wide
 * business setting, not to the printer.
 */
export default function LabelPrinterSettings() {
    const { t } = useTranslation();
    const { configuration } = useApi();
    const { API_BASE_URL, token } = useAuth();

    const printers = usePrinterSelection({ enabled: true });

    const [cashPoints, setCashPoints] = useState<{ id: number; name: string }[]>([]);
    const [cashPointId, setCashPointId] = useState<number | null>(null);
    const [stockOptions, setStockOptions] = useState<LabelStockOption[]>([]);
    const [stockId, setStockId] = useState<string | undefined>(undefined);
    const [storedQueue, setStoredQueue] = useState('');
    const [savingStock, setSavingStock] = useState(false);

    // So the failover toast fires once per change rather than on every reconcile tick.
    const announcedFailover = useRef<string | null>(null);

    useEffect(() => {
        const failedOverFrom = printers.view.failedOverFrom;

        if (failedOverFrom == null || failedOverFrom === announcedFailover.current) {
            return;
        }

        announcedFailover.current = failedOverFrom;
        message.warning(
            t('configuration.labelPrinter.failedOver', {
                previous: failedOverFrom,
                current:
                    printers.view.rows.find(row => row.effective)?.displayName ??
                    t('configuration.labelPrinter.noneSelected'),
            }),
        );
    }, [printers.view, t]);

    useEffect(() => {
        void fetch(`${API_BASE_URL}/cash-drawer/cashpoints`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(response => (response.ok ? response.json() : []))
            .then((rows: { id: number; name: string }[]) => {
                setCashPoints(rows);
                setCashPointId(current => current ?? rows[0]?.id ?? null);
            })
            .catch(() => message.error(t('configuration.labelPrinter.loadError')));
    }, [API_BASE_URL, token, t]);

    const loadStock = useCallback(
        async (id: number) => {
            const response = await configuration.getLabelPrinter(id);

            if (!response.success || response.data === undefined) {
                message.error(response.error ?? t('configuration.labelPrinter.loadError'));
                return;
            }

            setStockOptions(response.data.availableStock);
            setStockId(response.data.stockId ?? undefined);
            setStoredQueue(response.data.queue);
        },
        [configuration, t],
    );

    useEffect(() => {
        if (cashPointId !== null) {
            void loadStock(cashPointId);
        }
    }, [cashPointId, loadStock]);

    const handleChoose = async (printerId: string) => {
        const ok = await printers.choose(printerId);

        message[ok ? 'success' : 'error'](
            t(
                ok
                    ? 'configuration.labelPrinter.selected'
                    : 'configuration.labelPrinter.selectFailed',
            ),
        );
    };

    const handleSaveStock = async () => {
        if (cashPointId === null) {
            return;
        }

        setSavingStock(true);
        try {
            // The queue is no longer typed by anyone. It stays in the database because label
            // printing still requires a non-empty value per cash point, but the device that
            // receives the job is resolved by the local service — so this *mirrors* the effective
            // printer rather than selecting it, which is what stops the two diverging.
            //
            // With no effective printer the stored value is left alone: overwriting it with a
            // placeholder would replace a real record with a fiction, and an empty one would make
            // the cash point unable to print at all.
            const effectiveName = printers.view.rows.find(row => row.effective)?.displayName;

            const response = await configuration.saveLabelPrinter(cashPointId, {
                queue: effectiveName ?? storedQueue,
                stockId: stockId ?? null,
            });

            if (!response.success) {
                message.error(response.error ?? t('configuration.labelPrinter.saveError'));
                return;
            }

            setStockId(response.data?.stockId ?? undefined);
            message.success(t('configuration.labelPrinter.saved'));
        } finally {
            setSavingStock(false);
        }
    };

    const columns = [
        {
            title: t('configuration.labelPrinter.columnPrinter'),
            dataIndex: 'displayName',
            key: 'displayName',
            render: (name: string, row: PrinterRow) => (
                <Space direction="vertical" size={0}>
                    <Text strong={row.effective}>{name}</Text>
                    {row.model !== null && <Text type="secondary">{row.model}</Text>}
                </Space>
            ),
        },
        {
            title: t('configuration.labelPrinter.columnProvider'),
            dataIndex: 'provider',
            key: 'provider',
            render: (provider: string) => provider.toUpperCase(),
        },
        {
            title: t('configuration.labelPrinter.columnStatus'),
            dataIndex: 'health',
            key: 'health',
            render: (health: PrinterHealth, row: PrinterRow) => (
                <Space direction="vertical" size={0}>
                    <Tag color={HEALTH_COLOUR[health]}>
                        {t(`configuration.labelPrinter.health.${health}`)}
                    </Tag>
                    {row.statusReason !== null && <Text type="secondary">{row.statusReason}</Text>}
                </Space>
            ),
        },
        {
            title: t('configuration.labelPrinter.columnRole'),
            key: 'role',
            render: (_: unknown, row: PrinterRow) => (
                <Space>
                    {row.effective && (
                        <Tag color="blue">{t('configuration.labelPrinter.effective')}</Tag>
                    )}
                    {row.preferred && <Tag>{t('configuration.labelPrinter.preferred')}</Tag>}
                </Space>
            ),
        },
        {
            title: '',
            key: 'action',
            render: (_: unknown, row: PrinterRow) =>
                row.effective || !row.selectable ? null : (
                    <Button
                        size="small"
                        loading={printers.busy}
                        data-testid={`select-printer-${row.id}`}
                        onClick={() => void handleChoose(row.id)}
                    >
                        {t('configuration.labelPrinter.use')}
                    </Button>
                ),
        },
    ];

    return (
        <div data-testid="label-printer-panel">
            <Space align="baseline" style={{ justifyContent: 'space-between', width: '100%' }}>
                <div>
                    <Text strong>{t('configuration.labelPrinter.title')}</Text>
                    <br />
                    <Text type="secondary">{t('configuration.labelPrinter.description')}</Text>
                </div>
                <Button
                    icon={<ReloadOutlined />}
                    loading={printers.loading}
                    data-testid="refresh-printers"
                    onClick={() => void printers.reload(true)}
                >
                    {t('configuration.labelPrinter.refresh')}
                </Button>
            </Space>

            {!printers.view.canPrint && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 16 }}
                    data-testid="printer-blocked"
                    message={
                        printers.view.blockedReason ?? t('configuration.labelPrinter.noneConnected')
                    }
                />
            )}

            <Table
                rowKey="id"
                size="small"
                style={{ marginTop: 16 }}
                pagination={false}
                loading={printers.loading}
                columns={columns}
                dataSource={printers.view.rows}
                data-testid="printers-table"
                locale={{ emptyText: t('configuration.labelPrinter.noneDetected') }}
            />

            <Form layout="vertical" style={{ marginTop: 24 }} data-testid="label-stock-form">
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label={t('configuration.labelPrinter.cashPoint')}>
                            <Select
                                value={cashPointId ?? undefined}
                                onChange={setCashPointId}
                                options={cashPoints.map(cashPoint => ({
                                    value: cashPoint.id,
                                    label: `${cashPoint.id} — ${cashPoint.name}`,
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={10}>
                        <Form.Item
                            label={t('configuration.labelPrinter.stock')}
                            tooltip={t('configuration.labelPrinter.stockHint')}
                        >
                            <Select
                                allowClear
                                value={stockId}
                                onChange={setStockId}
                                placeholder={t('configuration.labelPrinter.stockPlaceholder')}
                                options={stockOptions.map(option => ({
                                    value: option.stockId,
                                    label: describeLabelStockOption(option),
                                }))}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item label=" ">
                            <Button
                                type="primary"
                                loading={savingStock}
                                data-testid="save-label-stock"
                                onClick={() => void handleSaveStock()}
                            >
                                {t('configuration.save')}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </div>
    );
}
